import React, { useState, useEffect, useRef, useCallback } from 'react'
import * as api from '../../services/api.js'
import { useThemeContext } from '../../context/ThemeContext.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import AppSelect from '../../components/AppSelect.jsx'
import FilterBar from '../../components/FilterBar.jsx'

// ── Constants ─────────────────────────────────────────────────────────────────

const IDEA_COLORS = [
  { label: 'Warm cream',    value: '#f5f0e8' },
  { label: 'Soft rose',     value: '#fde8e8' },
  { label: 'Pale sage',     value: '#e8f5ed' },
  { label: 'Lavender mist', value: '#ede8f5' },
  { label: 'Sky wash',      value: '#e8f0f5' },
  { label: 'Amber glow',    value: '#f5f0e0' },
]

const CATEGORIES = ['general', 'character', 'plot', 'world', 'dialogue', 'theme']
const CAT_LABELS  = { general: 'General', character: 'Character', plot: 'Plot', world: 'World', dialogue: 'Dialogue', theme: 'Theme' }
const STAGES      = [
  { id: 'raw',        label: 'Raw spark' },
  { id: 'developing', label: 'Developing' },
  { id: 'ready',      label: 'Ready to use' },
]
const STAGE_LABELS = { raw: 'Raw spark', developing: 'Developing', ready: 'Ready to use' }

const VIEWS = [
  { id: 'sticky',       label: 'Sticky notes',  icon: '🗒' },
  { id: 'columns',      label: 'Idea columns',  icon: '📋' },
  { id: 'mindmap',      label: 'Mind map',       icon: '🫧' },
  { id: 'constellation',label: 'Constellation', icon: '✦' },
]

const CATEGORY_COLORS = {
  character: '#a78bfa', plot: '#60a5fa', world: '#34d399',
  dialogue: '#fbbf24', theme: '#f87171', general: '#9ca3af',
}
const CONSTELLATION_COLORS = {
  character: 'rgba(167,139,250,0.9)', plot: 'rgba(96,165,250,0.9)',
  world: 'rgba(52,211,153,0.9)', dialogue: 'rgba(251,191,36,0.9)',
  theme: 'rgba(248,113,113,0.9)', general: 'rgba(209,213,219,0.9)',
}

function randomPos() {
  return {
    x: 20 + Math.random() * 380,
    y: 20 + Math.random() * 280,
  }
}

// ── IdeaPanel ─────────────────────────────────────────────────────────────────

function IdeaPanel({ idea, prefilledStage, projectId, onClose, onSaved, onDeleted }) {
  const isNew = !idea
  const [form, setForm] = useState({
    text:     idea?.text     || '',
    category: idea?.category || 'general',
    stage:    idea?.stage    || prefilledStage || 'raw',
    color:    idea?.color    || '#f5f0e8',
  })
  const [open, setOpen]   = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const textRef           = useRef(null)

  useEffect(() => {
    setTimeout(() => setOpen(true), 10)
    setTimeout(() => textRef.current?.focus(), 60)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') handleClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  function handleClose() {
    setOpen(false)
    setTimeout(onClose, 260)
  }

  async function handleSave() {
    if (!form.text.trim()) return
    if (isNew) {
      const pos = randomPos()
      const payload = {
        ...form,
        text: form.text.trim(),
        project_id: projectId || null,
        position_x: pos.x,
        position_y: pos.y,
        size: 'medium',
      }
      const res = await api.saveBrainstormIdea(payload)
      onSaved({ ...payload, id: res?.id || Date.now() })
    } else {
      const updates = { ...form, text: form.text.trim() }
      await api.updateBrainstormIdea(idea.id, updates)
      onSaved({ ...idea, ...updates })
    }
    handleClose()
  }

  async function handleDelete() {
    setShowDeleteConfirm(true)
  }

  async function confirmDelete() {
    setShowDeleteConfirm(false)
    await api.deleteBrainstormIdea(idea.id)
    onDeleted(idea.id)
    handleClose()
  }

  return (
    <>
      <div
        className="b-panel-backdrop"
        onClick={handleClose}
        style={{ opacity: open ? 1 : 0, transition: 'opacity 0.25s' }}
      />
      <div className={`b-idea-panel${open ? ' open' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.1rem' }}>
          <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--stone)' }}>
            {isNew ? 'New idea' : 'Edit idea'}
          </span>
          <button className="panel-close-btn" onClick={handleClose}>✕</button>
        </div>

        {/* Text */}
        <textarea
          ref={textRef}
          className="b-panel-textarea"
          rows={4}
          placeholder="What's the idea?"
          value={form.text}
          onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
        />

        {/* Category */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div className="b-panel-label">Category</div>
          <AppSelect
            value={form.category}
            onChange={v => setForm(f => ({ ...f, category: v }))}
            options={CATEGORIES.map(c => ({ value: c, label: CAT_LABELS[c] }))}
          />
        </div>

        {/* Stage */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div className="b-panel-label">Stage</div>
          <AppSelect
            value={form.stage}
            onChange={v => setForm(f => ({ ...f, stage: v }))}
            options={STAGES.map(s => ({ value: s.id, label: s.label }))}
          />
        </div>

        {/* Color swatches */}
        <div style={{ marginBottom: '1.1rem' }}>
          <div className="b-panel-label">Colour</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {IDEA_COLORS.map(c => (
              <button
                key={c.value}
                title={c.label}
                onClick={() => setForm(f => ({ ...f, color: c.value }))}
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: c.value,
                  border: form.color === c.value ? '2.5px solid var(--bark)' : '1.5px solid rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  transition: 'transform 0.1s',
                  transform: form.color === c.value ? 'scale(1.15)' : 'scale(1)',
                }}
              />
            ))}
          </div>
        </div>

        <button
          className="b-panel-save-btn"
          onClick={handleSave}
          disabled={!form.text.trim()}
          style={{ opacity: form.text.trim() ? 1 : 0.45 }}
        >
          {isNew ? 'Add idea' : 'Save changes'}
        </button>

        {!isNew && (
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '0.9rem', marginTop: '1rem' }}>
            <button
              onClick={handleDelete}
              style={{ fontSize: '0.72rem', color: '#DC2626', background: 'transparent', border: '1px solid #DC2626', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
            >
              Delete idea
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete this idea?"
        message="This cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  )
}

// ── StickyNotesView ────────────────────────────────────────────────────────────

function StickyNotesView({ ideas, onEdit, onPositionSave, pid }) {
  const [positions, setPositions]   = useState({})
  const [catFilter, setCatFilter]   = useState('all')
  const dragging = useRef(null)

  // Assign random positions to unpositioned ideas
  useEffect(() => {
    const initial = {}
    ideas.forEach(idea => {
      const x = idea.position_x || 0
      const y = idea.position_y || 0
      if (x === 0 && y === 0) {
        const p = randomPos()
        initial[idea.id] = p
      } else {
        initial[idea.id] = { x, y }
      }
    })
    setPositions(initial)
  }, [ideas.map(i => i.id).join(',')])

  function getPos(idea) {
    return positions[idea.id] || { x: idea.position_x || 0, y: idea.position_y || 0 }
  }

  const handleMouseDown = useCallback((e, idea) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    const pos = getPos(idea)
    const startX = e.clientX - pos.x
    const startY = e.clientY - pos.y
    dragging.current = idea.id

    const onMove = (ev) => {
      const newX = Math.max(0, ev.clientX - startX)
      const newY = Math.max(0, ev.clientY - startY)
      setPositions(p => ({ ...p, [idea.id]: { x: newX, y: newY } }))
    }
    const onUp = () => {
      if (dragging.current === idea.id) {
        setPositions(p => {
          const finalPos = p[idea.id]
          if (finalPos) {
            api.updateBrainstormIdea(idea.id, { position_x: finalPos.x, position_y: finalPos.y })
          }
          return p
        })
      }
      dragging.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [positions])

  const visible = catFilter === 'all'
    ? ideas
    : ideas.filter(i => i.category === catFilter)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', height: '100%' }}>
      {/* Category filter */}
      <div style={{ padding: '0.5rem 0.75rem' }}>
        <FilterBar
          filters={[{ key: 'all', label: 'All' }, ...CATEGORIES.map(c => ({ key: c, label: CAT_LABELS[c] }))]}
          active={catFilter}
          onChange={setCatFilter}
        />
      </div>

      {/* Board */}
      <div className="brainstorm-board" style={{ flex: 1, minHeight: 480 }}>
        {visible.length === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: 'var(--stone)', fontSize: '0.88rem', fontStyle: 'italic', fontFamily: 'var(--font-serif)' }}>
              No ideas yet — add one above.
            </p>
          </div>
        )}
        {visible.map(idea => {
          const pos = getPos(idea)
          return (
            <div
              key={idea.id}
              className="sticky-note"
              style={{
                left: pos.x,
                top:  pos.y,
                background: idea.color || '#f5f0e8',
                cursor: dragging.current === idea.id ? 'grabbing' : 'grab',
              }}
              onMouseDown={e => handleMouseDown(e, idea)}
              onDoubleClick={() => onEdit(idea)}
            >
              <div className="sticky-note-category">{CAT_LABELS[idea.category] || idea.category}</div>
              <div className="sticky-note-text">{idea.text}</div>
              <div className="sticky-note-footer">
                <span className="sticky-note-stage">{STAGE_LABELS[idea.stage] || idea.stage}</span>
                <button
                  className="sticky-note-edit-btn"
                  onClick={e => { e.stopPropagation(); onEdit(idea) }}
                  title="Edit"
                >✎</button>
              </div>
            </div>
          )
        })}
        <div className="brainstorm-view-switcher" />
      </div>
    </div>
  )
}

// ── ColumnsView ────────────────────────────────────────────────────────────────

function ColumnsView({ ideas, onEdit, onAddWithStage, onStageChange }) {
  const cols = [
    { stage: 'raw',        label: 'Raw sparks',    hint: 'Unfiltered, anything goes' },
    { stage: 'developing', label: 'Developing',    hint: 'Taking shape, needs work' },
    { stage: 'ready',      label: 'Ready to use',  hint: 'Polished and actionable' },
  ]

  function move(idea, dir) {
    const order = ['raw', 'developing', 'ready']
    const idx = order.indexOf(idea.stage)
    const next = order[idx + dir]
    if (next) onStageChange(idea.id, next)
  }

  return (
    <div className="brainstorm-columns">
      {cols.map(col => {
        const colIdeas = ideas.filter(i => i.stage === col.stage)
        return (
          <div key={col.stage} className="brainstorm-col">
            <div className="brainstorm-col-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="brainstorm-col-title">{col.label}</span>
                <span className="brainstorm-col-count">{colIdeas.length}</span>
              </div>
              <div className="brainstorm-col-hint">{col.hint}</div>
            </div>

            {colIdeas.length === 0 ? (
              <div className="brainstorm-col-empty">
                <p>No ideas yet</p>
                <button onClick={() => onAddWithStage(col.stage)} className="b-filter-chip" style={{ marginTop: 6 }}>
                  + Add one
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {colIdeas.map(idea => (
                  <div key={idea.id} className="brainstorm-col-card" style={{ borderLeftColor: CATEGORY_COLORS[idea.category] || '#9ca3af' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                      <span className="brainstorm-col-card-cat">{CAT_LABELS[idea.category]}</span>
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        {col.stage !== 'raw' && (
                          <button className="b-arrow-btn" onClick={() => move(idea, -1)} title="Move back">←</button>
                        )}
                        {col.stage !== 'ready' && (
                          <button className="b-arrow-btn" onClick={() => move(idea, 1)} title="Move forward">→</button>
                        )}
                        <button className="b-arrow-btn" onClick={() => onEdit(idea)} title="Edit">✎</button>
                      </div>
                    </div>
                    <div className="brainstorm-col-card-text">{idea.text}</div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => onAddWithStage(col.stage)}
              className="b-col-add-btn"
            >
              + Add
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ── MindMapView ────────────────────────────────────────────────────────────────

function MindMapView({ ideas, projectName, onEdit }) {
  const { theme } = useThemeContext()
  const containerRef = useRef(null)
  const canvasRef    = useRef(null)
  const [nodes,    setNodes]    = useState([])
  const [offset,   setOffset]   = useState({ x: 0, y: 0 })
  const [scale,    setScale]    = useState(1)
  const dragNode   = useRef(null)
  const dragPan    = useRef(null)

  // Build nodes from ideas
  useEffect(() => {
    if (!containerRef.current) return
    const W = containerRef.current.clientWidth  || 700
    const H = containerRef.current.clientHeight || 450
    const cx = W / 2
    const cy = H / 2
    const r  = Math.min(W, H) * 0.32

    setNodes(ideas.map((idea, i) => ({
      ...idea,
      x: idea.position_x && idea.position_x !== 0
        ? idea.position_x
        : cx + r * Math.cos((2 * Math.PI * i) / Math.max(ideas.length, 1) - Math.PI / 2),
      y: idea.position_y && idea.position_y !== 0
        ? idea.position_y
        : cy + r * Math.sin((2 * Math.PI * i) / Math.max(ideas.length, 1) - Math.PI / 2),
    })))
  }, [ideas.map(i => i.id).join(',')])

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return
    const dpr = window.devicePixelRatio || 1
    const W = container.clientWidth
    const H = container.clientHeight
    canvas.width  = W * dpr
    canvas.height = H * dpr
    canvas.style.width  = `${W}px`
    canvas.style.height = `${H}px`

    const ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)

    const bg   = theme.cssVars['--bg']   || '#EEF5EE'
    const surf = theme.cssVars['--surf'] || '#D9EDD9'
    const ink  = theme.cssVars['--ink']  || '#0D2B12'
    const bark = theme.cssVars['--bark'] || '#2E7D3E'
    const acc  = theme.cssVars['--accent']|| '#238b45'

    // Background
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, W, H)

    const cx = W / 2
    const cy = H / 2

    ctx.save()
    ctx.translate(offset.x, offset.y)
    ctx.scale(scale, scale)

    // Edges
    nodes.forEach(node => {
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.lineTo(node.x, node.y)
      ctx.strokeStyle = 'rgba(0,0,0,0.1)'
      ctx.lineWidth = 1.5 / scale
      ctx.stroke()
    })

    // Idea nodes
    nodes.forEach(node => {
      const nW = 120, nH = 44
      const nx = node.x - nW / 2
      const ny = node.y - nH / 2
      const r  = 8
      // Rounded rect
      ctx.beginPath()
      ctx.moveTo(nx + r, ny)
      ctx.lineTo(nx + nW - r, ny)
      ctx.quadraticCurveTo(nx + nW, ny, nx + nW, ny + r)
      ctx.lineTo(nx + nW, ny + nH - r)
      ctx.quadraticCurveTo(nx + nW, ny + nH, nx + nW - r, ny + nH)
      ctx.lineTo(nx + r, ny + nH)
      ctx.quadraticCurveTo(nx, ny + nH, nx, ny + nH - r)
      ctx.lineTo(nx, ny + r)
      ctx.quadraticCurveTo(nx, ny, nx + r, ny)
      ctx.closePath()
      ctx.fillStyle = node.color || '#f5f0e8'
      ctx.fill()
      ctx.strokeStyle = 'rgba(0,0,0,0.12)'
      ctx.lineWidth = 1 / scale
      ctx.stroke()

      // Category dot
      const dotColor = CATEGORY_COLORS[node.category] || '#9ca3af'
      ctx.beginPath()
      ctx.arc(nx + 10, ny + 10, 4, 0, Math.PI * 2)
      ctx.fillStyle = dotColor
      ctx.fill()

      // Text
      ctx.fillStyle = '#333'
      ctx.font = `${11 / scale}px sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const label = node.text.length > 20 ? node.text.slice(0, 20) + '…' : node.text
      ctx.fillText(label, node.x, node.y)
    })

    // Central node
    ctx.beginPath()
    ctx.arc(cx, cy, 30 / scale, 0, Math.PI * 2)
    ctx.fillStyle = acc
    ctx.fill()
    ctx.fillStyle = 'white'
    ctx.font = `bold ${11 / scale}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const label = (projectName || 'My ideas').slice(0, 12)
    ctx.fillText(label, cx, cy)

    ctx.restore()
  }, [nodes, offset, scale, theme])

  function toWorld(cx, cy) {
    return { x: (cx - offset.x) / scale, y: (cy - offset.y) / scale }
  }

  function hitNode(wx, wy) {
    return nodes.find(n => Math.abs(wx - n.x) < 64 && Math.abs(wy - n.y) < 26)
  }

  function onMouseDown(e) {
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    const { x: wx, y: wy } = toWorld(cx, cy)
    const node = hitNode(wx, wy)
    if (node) {
      dragNode.current = { id: node.id, startX: cx, startY: cy, origX: node.x, origY: node.y }
    } else {
      dragPan.current = { startX: cx, startY: cy, origOX: offset.x, origOY: offset.y }
    }
  }

  function onMouseMove(e) {
    if (!dragNode.current && !dragPan.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    if (dragNode.current) {
      const dx = (cx - dragNode.current.startX) / scale
      const dy = (cy - dragNode.current.startY) / scale
      setNodes(prev => prev.map(n => n.id === dragNode.current.id
        ? { ...n, x: dragNode.current.origX + dx, y: dragNode.current.origY + dy }
        : n
      ))
    } else if (dragPan.current) {
      setOffset({ x: dragPan.current.origOX + cx - dragPan.current.startX, y: dragPan.current.origOY + cy - dragPan.current.startY })
    }
  }

  function onMouseUp(e) {
    if (dragNode.current) {
      const node = nodes.find(n => n.id === dragNode.current.id)
      if (node) api.updateBrainstormIdea(node.id, { position_x: node.x, position_y: node.y })
      dragNode.current = null
    }
    if (dragPan.current) {
      const rect = canvasRef.current.getBoundingClientRect()
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top
      const moved = Math.abs(cx - dragPan.current.startX) + Math.abs(cy - dragPan.current.startY)
      if (moved < 5) {
        // Click on empty canvas — no-op
      }
      dragPan.current = null
    }
  }

  function onClick(e) {
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    const { x: wx, y: wy } = toWorld(cx, cy)
    const node = hitNode(wx, wy)
    if (node) onEdit(node)
  }

  function onWheel(e) {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    setScale(s => Math.min(Math.max(s * delta, 0.3), 3))
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: 480, borderRadius: 12, overflow: 'hidden', background: 'var(--bg)', border: '0.5px solid rgba(0,0,0,0.1)' }}>
      {ideas.length === 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <p style={{ color: 'var(--stone)', fontSize: '0.88rem', fontStyle: 'italic' }}>No ideas yet — add one above.</p>
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{ display: 'block', cursor: 'default' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onClick={onClick}
        onWheel={onWheel}
      />
    </div>
  )
}

// ── ConstellationView ─────────────────────────────────────────────────────────

function ConstellationView({ ideas, onEdit }) {
  const containerRef = useRef(null)
  const canvasRef    = useRef(null)
  const [nodes,  setNodes]  = useState([])
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [scale,  setScale]  = useState(1)
  const dragNode = useRef(null)
  const dragPan  = useRef(null)

  useEffect(() => {
    setNodes(ideas.map((idea, i) => ({
      ...idea,
      x: (idea.position_x && idea.position_x !== 0)
        ? idea.position_x
        : 80 + (i % 5) * 130 + Math.random() * 40,
      y: (idea.position_y && idea.position_y !== 0)
        ? idea.position_y
        : 80 + Math.floor(i / 5) * 130 + Math.random() * 40,
    })))
  }, [ideas.map(i => i.id).join(',')])

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return
    const dpr = window.devicePixelRatio || 1
    const W = container.clientWidth
    const H = container.clientHeight
    canvas.width  = W * dpr
    canvas.height = H * dpr
    canvas.style.width  = `${W}px`
    canvas.style.height = `${H}px`

    const ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)

    // Dark background always
    ctx.fillStyle = '#1a1a2e'
    ctx.fillRect(0, 0, W, H)

    ctx.save()
    ctx.translate(offset.x, offset.y)
    ctx.scale(scale, scale)

    // Lines between same-category ideas
    const byCat = {}
    nodes.forEach(n => { (byCat[n.category] = byCat[n.category] || []).push(n) })
    Object.values(byCat).forEach(group => {
      if (group.length < 2) return
      for (let i = 0; i < group.length - 1; i++) {
        ctx.beginPath()
        ctx.moveTo(group[i].x, group[i].y)
        ctx.lineTo(group[i + 1].x, group[i + 1].y)
        ctx.strokeStyle = 'rgba(255,255,255,0.06)'
        ctx.lineWidth = 1 / scale
        ctx.stroke()
      }
    })

    // Stars
    nodes.forEach(node => {
      const radius = node.stage === 'ready' ? 18 : node.stage === 'developing' ? 13 : 9
      const color = CONSTELLATION_COLORS[node.category] || CONSTELLATION_COLORS.general

      // Glow
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius * 2.5, 0, Math.PI * 2)
      ctx.fillStyle = color.replace('0.9)', '0.15)')
      ctx.fill()

      // Star
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()

      // Label
      ctx.fillStyle = 'rgba(255,255,255,0.8)'
      ctx.font = `${11 / scale}px sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      const label = node.text.length > 16 ? node.text.slice(0, 16) + '…' : node.text
      ctx.fillText(label, node.x, node.y + radius + 5 / scale)
    })

    ctx.restore()
  }, [nodes, offset, scale])

  function toWorld(cx, cy) {
    return { x: (cx - offset.x) / scale, y: (cy - offset.y) / scale }
  }

  function hitNode(wx, wy) {
    return nodes.find(n => {
      const r = n.stage === 'ready' ? 18 : n.stage === 'developing' ? 13 : 9
      const dist = Math.sqrt((wx - n.x) ** 2 + (wy - n.y) ** 2)
      return dist < r + 8
    })
  }

  function onMouseDown(e) {
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    const { x: wx, y: wy } = toWorld(cx, cy)
    const node = hitNode(wx, wy)
    if (node) {
      dragNode.current = { id: node.id, startX: cx, startY: cy, origX: node.x, origY: node.y }
    } else {
      dragPan.current = { startX: cx, startY: cy, origOX: offset.x, origOY: offset.y }
    }
  }

  function onMouseMove(e) {
    if (!dragNode.current && !dragPan.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    if (dragNode.current) {
      const dx = (cx - dragNode.current.startX) / scale
      const dy = (cy - dragNode.current.startY) / scale
      setNodes(prev => prev.map(n => n.id === dragNode.current.id
        ? { ...n, x: dragNode.current.origX + dx, y: dragNode.current.origY + dy }
        : n
      ))
    } else if (dragPan.current) {
      setOffset({ x: dragPan.current.origOX + cx - dragPan.current.startX, y: dragPan.current.origOY + cy - dragPan.current.startY })
    }
  }

  function onMouseUp(e) {
    if (dragNode.current) {
      const node = nodes.find(n => n.id === dragNode.current.id)
      if (node) api.updateBrainstormIdea(node.id, { position_x: node.x, position_y: node.y })
      dragNode.current = null
    }
    dragPan.current = null
  }

  function onClick(e) {
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    const { x: wx, y: wy } = toWorld(cx, cy)
    const node = hitNode(wx, wy)
    if (node) onEdit(node)
  }

  function onWheel(e) {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    setScale(s => Math.min(Math.max(s * delta, 0.3), 3))
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: 480, borderRadius: 12, overflow: 'hidden' }}>
      {ideas.length === 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 2 }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', fontStyle: 'italic' }}>No ideas yet — add one above.</p>
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{ display: 'block', cursor: 'default' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onClick={onClick}
        onWheel={onWheel}
      />
    </div>
  )
}

// ── Brainstorm main ────────────────────────────────────────────────────────────

export default function Brainstorm({ pid, projectName }) {
  const [ideas,       setIdeas]       = useState([])
  const [activeView,  setActiveView]  = useState(() =>
    localStorage.getItem('brainstormView') || 'sticky'
  )
  const [viewOpen,    setViewOpen]    = useState(false)
  const [panelOpen,   setPanelOpen]   = useState(false)
  const [editIdea,    setEditIdea]    = useState(null)
  const [prefilledStage, setPrefilledStage] = useState('raw')

  useEffect(() => {
    api.getBrainstormIdeas(pid).then(d => setIdeas(d || []))
  }, [pid])

  function openAdd(stage = 'raw') {
    setEditIdea(null)
    setPrefilledStage(stage)
    setPanelOpen(true)
  }

  function openEdit(idea) {
    setEditIdea(idea)
    setPanelOpen(true)
  }

  function handleSaved(saved) {
    setIdeas(prev => {
      const exists = prev.find(i => i.id === saved.id)
      return exists
        ? prev.map(i => i.id === saved.id ? saved : i)
        : [saved, ...prev]
    })
  }

  function handleDeleted(id) {
    setIdeas(prev => prev.filter(i => i.id !== id))
  }

  function handleStageChange(id, newStage) {
    setIdeas(prev => prev.map(i => i.id === id ? { ...i, stage: newStage } : i))
    api.updateBrainstormIdea(id, { stage: newStage })
  }

  function switchView(id) {
    setActiveView(id)
    localStorage.setItem('brainstormView', id)
    setViewOpen(false)
  }

  const currentView = VIEWS.find(v => v.id === activeView) || VIEWS[0]

  return (
    <div className="brainstorm-wrap">
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--stone)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {ideas.length} idea{ideas.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button className="b-add-btn" onClick={() => openAdd()}>+ Idea</button>
      </div>

      {/* Content area */}
      <div style={{ position: 'relative' }}>
        {activeView === 'sticky' && (
          <StickyNotesView ideas={ideas} onEdit={openEdit} pid={pid} />
        )}
        {activeView === 'columns' && (
          <ColumnsView
            ideas={ideas}
            onEdit={openEdit}
            onAddWithStage={openAdd}
            onStageChange={handleStageChange}
          />
        )}
        {activeView === 'mindmap' && (
          <MindMapView ideas={ideas} projectName={projectName} onEdit={openEdit} />
        )}
        {activeView === 'constellation' && (
          <ConstellationView ideas={ideas} onEdit={openEdit} />
        )}

        {/* View switcher pill */}
        <div className="brainstorm-view-switcher">
          <button className="view-pill-btn" onClick={() => setViewOpen(o => !o)}>
            {currentView.icon} {currentView.label} ▾
          </button>
          {viewOpen && (
            <div className="view-dropdown-up">
              {[...VIEWS].reverse().map(v => (
                <div
                  key={v.id}
                  className={`view-option${v.id === activeView ? ' active' : ''}`}
                  onClick={() => switchView(v.id)}
                >
                  <span>{v.icon}</span>
                  <span>{v.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Idea panel */}
      {panelOpen && (
        <IdeaPanel
          key={editIdea?.id || 'new'}
          idea={editIdea}
          prefilledStage={prefilledStage}
          projectId={pid}
          onClose={() => setPanelOpen(false)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}
